import { useState } from 'react';
import * as React from 'react';
import TabListNavItem, { type ITabListNavItemProps } from './components/TabListNavItem';
import TabListTabContent, { type ITabListTabContentProps } from './components/TabListTabContent';
import TabHelper from './tab.helper';
import './style.scss';

interface ITabListProps {
  id: string;
  activeIndex?: number;
  renderTabsAsDropdownOnMobile?: boolean;
  dropdownLabel?: string;
  dropDownLabelledBy?: string;
  onSelectedChange?: (index: number) => void;
  navItems: React.ReactElement<ITabListNavItemProps>[];
  tabContents: React.ReactElement<ITabListTabContentProps>[];
  className?: string;
  disableNavItems?: boolean;
}

function TabList({
  id,
  activeIndex = 0,
  renderTabsAsDropdownOnMobile = false,
  dropdownLabel = undefined,
  dropDownLabelledBy = undefined,
  onSelectedChange = undefined,
  navItems,
  tabContents,
  className,
  disableNavItems = false,
}: ITabListProps) {
  const [focus, setFocus] = useState<boolean>(false);

  function handleOnNavItemActivated(index: number) {
    onSelectedChange?.(index);
  }

  function getNavItems() {
    return navItems.map((item: React.ReactElement<ITabListNavItemProps>, index) => (
      <TabListNavItem
        key={item.key}
        id={TabHelper.getTabListNavId(id, index)}
        ariaControls={TabHelper.getTabListTabId(id, index)}
        index={index}
        activeIndex={activeIndex}
        focus={focus}
        disabled={disableNavItems}
        onActivate={(active) => {
          handleOnNavItemActivated(active);
          item.props.onActivate?.(active);
        }}
        onKeyDown={(e) => {
          if (disableNavItems) return;

          if (e.key === 'ArrowLeft') {
            const newActiveIndex = (((activeIndex - 1) % navItems.length) + navItems.length) % navItems.length;
            handleOnNavItemActivated(newActiveIndex); // https://stackoverflow.com/questions/4467539/javascript-modulo-gives-a-negative-result-for-negative-numbers
            (navItems[newActiveIndex] as React.ReactElement<ITabListNavItemProps>).props.onActivate?.(newActiveIndex);
            setFocus(true);
          } else if (e.key === 'ArrowRight') {
            const newActiveIndex = (activeIndex + 1) % navItems.length;
            handleOnNavItemActivated(newActiveIndex);
            (navItems[newActiveIndex] as React.ReactElement<ITabListNavItemProps>).props.onActivate?.(newActiveIndex);
            setFocus(true);
          }
        }}
      >
        {item.props.children}
      </TabListNavItem>
    ));
  }

  function getTabContents() {
    return tabContents.map((item: React.ReactElement<ITabListTabContentProps>, index) => (
      <TabListTabContent key={item.key} id={TabHelper.getTabListTabId(id, index)} index={index} activeIndex={activeIndex} navId={TabHelper.getTabListNavId(id, index)}>
        {item.props.children}
      </TabListTabContent>
    ));
  }

  return (
    <>
      {navItems?.length > 0 && (
        <>
          <ul
            role="tablist"
            className={`tab-list__list nav nav-tabs list-unstyled ${renderTabsAsDropdownOnMobile ? 'd-none d-md-flex' : 'd-flex'} ${className ?? ''}`}
            aria-owns={navItems.map((_, index) => TabHelper.getTabListNavId(id, index)).join(' ')}
          >
            {getNavItems()}
          </ul>

          {renderTabsAsDropdownOnMobile && (
            <div className="d-md-none">
              {dropdownLabel && (
                <label id={`${id}-dropdown-label`} htmlFor={`${id}-dropdown`} className="form-label">
                  {dropdownLabel}
                </label>
              )}
              <select
                id={`${id}-dropdown`}
                className="form-select"
                onChange={(element) => {
                  handleOnNavItemActivated(parseInt(element.target.value, 10));
                }}
                value={activeIndex}
                aria-labelledby={dropDownLabelledBy || `${id}-dropdown-label`}
              >
                {navItems.map((item: React.ReactElement<ITabListNavItemProps>, index) => (
                  <option key={`select-${item.key}`} aria-controls={TabHelper.getTabListTabId(id, index)} value={index}>
                    {item.props.children}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
      <div className="pt-3 tab-list__content tab-content">{getTabContents()}</div>
    </>
  );
}

export default TabList;
